import { useCallback, useMemo } from 'react';
import { atom, useAtom } from 'jotai';
import { useWindowSize } from '~/modules/root';

type ChatOpenState = 'open' | 'closed' | 'hidden';

export const DRAWER_WIDTH_REM = 30;
export const CHAT_BOX_OPEN_WIDTH_PX = 480;
export const CHAT_BOX_CLOSED_WIDTH_PX = 240;
export const SPACING_PX = 16;

/**
 * The chat box(es) open state.
 * - open: The chat messages drawer is open.
 * - closed: The chat messages drawer is closed but the button is visible.
 * - hidden: The chat messages drawer button is hidden.
 */
const chatOpenStateAtom = atom<ChatOpenState>('closed');
/**
 * The list of open chat conversations IDs and their open state.
 */
const chatConversationsListAtom = atom<{ id: number; isOpen: boolean }[]>([]);
/**
 * The pending message to be sent when the user sends a message in a new message
 * chat box. When the chat box with this conversation is opened, the message will
 * be sent automatically.
 */
const pendingMessageAtom = atom<{
    conversationID: number;
    message: string;
    attachments: File[];
} | null>(null);

/**
 * Hook to handle the website chat box functionality. This includes the chat box(es)
 * open state, and the list of open chat conversations.
 */
export function useChat() {
    const [chatOpenState, setChatOpenState] = useAtom(chatOpenStateAtom);
    const [chatConversationsList, setChatConversationsList] = useAtom(
        chatConversationsListAtom
    );
    const [pendingMessage, setPendingMessage] = useAtom(pendingMessageAtom);
    const { width: windowWidth = 1920 } = useWindowSize();
    const availableSpace = useMemo(
        () => calculateAvailableSpace(windowWidth, chatConversationsList),
        [windowWidth, chatConversationsList]
    );

    const isChatBoxOpen = useCallback(
        (conversationID: number): boolean => {
            return chatConversationsList.some(
                (c) => c.id === conversationID && c.isOpen
            );
        },
        [chatConversationsList]
    );

    const openChatBox = useCallback(
        (conversationID: number) => {
            setChatConversationsList((prev) => {
                const currentState = prev.find((c) => c.id === conversationID);
                if (currentState?.isOpen) {
                    // Already open, do nothing.
                    return prev;
                }
                // TODO: This is not perfect, will need some work later.
                const newState = [...prev];
                // Determine if we need to close or minimise another chat box to make space.
                const requiredSpace = currentState
                    ? CHAT_BOX_OPEN_WIDTH_PX - CHAT_BOX_CLOSED_WIDTH_PX
                    : CHAT_BOX_OPEN_WIDTH_PX + SPACING_PX;
                const spaceDeficit = requiredSpace - availableSpace;
                if (
                    spaceDeficit >
                    CHAT_BOX_OPEN_WIDTH_PX - CHAT_BOX_CLOSED_WIDTH_PX
                ) {
                    // If the space deficit is large, close the oldest chat box.
                    const indexToRemove = newState.findIndex(
                        (c) => c.id !== conversationID
                    );
                    if (indexToRemove !== -1) {
                        newState.splice(indexToRemove, 1);
                    }
                } else if (spaceDeficit > 0) {
                    // If the space deficit is small, minimise the oldest chat box.
                    const indexToMinimise = newState.findIndex(
                        (c) => c.id !== conversationID && c.isOpen
                    );
                    if (indexToMinimise !== -1) {
                        newState[indexToMinimise] = {
                            ...newState[indexToMinimise]!,
                            isOpen: false,
                        };
                    } else {
                        // If the other chat boxes are all minimised, close the oldest chat box.
                        const indexToRemove = newState.findIndex(
                            (c) => c.id !== conversationID
                        );
                        if (indexToRemove !== -1) {
                            newState.splice(indexToRemove, 1);
                        }
                    }
                }
                // If the chat box is minimised, open it.
                if (currentState) {
                    return newState.map((c) =>
                        c.id === conversationID ? { ...c, isOpen: true } : c
                    );
                }
                // If the chat box is not open, open it.
                return [...newState, { id: conversationID, isOpen: true }];
            });
        },
        [availableSpace, setChatConversationsList]
    );

    const convertNewMessageToConversation = useCallback(
        (conversationID: number) => {
            if (conversationID <= 0) return;
            setChatConversationsList((prev) => {
                return prev
                    .filter((c) => c.id !== conversationID)
                    .map((c) =>
                        c.id === 0 ? { id: conversationID, isOpen: true } : c
                    );
            });
        },
        [setChatConversationsList]
    );

    return {
        /**
         * The chat box open state.
         */
        chatOpenState,
        /**
         * The list of open chat conversations IDs.
         */
        chatConversationsList,
        /**
         * The pending message to be sent when the chat box with this
         * conversation is opened. This is set when the user sends a message in
         * the new message chat box in an existing conversation.
         */
        pendingMessage,
        /**
         * Set the chat box open state.
         */
        setChatOpenState,
        /**
         * Check if the chat box for a conversation is open.
         */
        isChatBoxOpen,
        /**
         * Open the chat box for a conversation. If there is no available screen
         * space, the oldest chat box(es) will be minimised or closed. Pass `0`
         * as the conversation ID to create a new conversation.
         */
        openChatBox,
        /**
         * Minimize the chat box for a conversation.
         */
        minimizeChatBox: useCallback(
            (conversationID: number) => {
                setChatConversationsList((prev) => {
                    return prev.map((c) =>
                        c.id === conversationID ? { ...c, isOpen: false } : c
                    );
                });
            },
            [setChatConversationsList]
        ),
        /**
         * Close the chat box for a conversation.
         */
        closeChatBox: useCallback(
            (conversationID: number) => {
                setChatConversationsList((prev) => {
                    return prev.filter((c) => c.id !== conversationID);
                });
            },
            [setChatConversationsList]
        ),
        /**
         * Set the pending message to be sent when the chat box with this
         * conversation is opened. Call this when the user sends a message in
         * the new message chat box in an existing conversation.
         */
        setPendingMessage,
        /**
         * Convert the new message chat box to a conversation chat box. If the
         * new message chat box is not open, this will do nothing except close
         * the conversation chat box it exists.
         */
        convertNewMessageToConversation,
    };
}

/**
 * Calculate the available space for new chat boxes. This is calculated by
 * taking the window width and subtracting the width of the other chat boxes.
 * @param windowWidth The window width.
 * @returns The available space for new chat boxes in pixels. The value is
 *  clamped between 0 and 1920.
 */
function calculateAvailableSpace(
    windowWidth: number,
    chatBoxes: { isOpen: boolean }[]
): number {
    const rootFontSize =
        typeof window !== 'undefined'
            ? parseFloat(getComputedStyle(document.documentElement).fontSize)
            : 16; // fallback to 16px for SSR
    const drawerWidthPx = DRAWER_WIDTH_REM * rootFontSize;

    const chatBoxesWidthPx = chatBoxes.reduce((acc, chatBox) => {
        return (
            acc +
            (chatBox.isOpen
                ? CHAT_BOX_OPEN_WIDTH_PX
                : CHAT_BOX_CLOSED_WIDTH_PX) +
            SPACING_PX
        );
    }, 0);

    return Math.min(
        Math.max(windowWidth - drawerWidthPx - chatBoxesWidthPx, 0),
        1920
    );
}
